import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProjectStatusLog, useRunStatusSweep } from '@/api/projects';
import { useProjectLabels } from '@/components/projects/ProjectKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, projectPath } from '@/lib/routes';

/**
 * Admin → PSA → Project status log: what the automatic progress status (Projects.md §5) changed — or, in
 * dry run, would have changed. The mode lives in Admin → PSA → Settings (psa.autoProjectStatus).
 */
export default function AdminProjectStatusPage() {
  const { data, isLoading } = useProjectStatusLog();
  const sweep = useRunStatusSweep();
  const labels = useProjectLabels();
  const mode = data?.mode;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-foreground">Project status log</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Cosmos works out each live project's progress (estimated hours of completed tasks) and keeps its Autotask status on In Progress 10, 25, 50 or 75%.
          It never sets New, Pending Completion or Complete. Mode: <b className="text-foreground">{mode === 'dry-run' ? 'dry run (logs only)' : mode ?? '…'}</b>, changed in{' '}
          <Link to={ROUTES.admin.psa} className="underline">PSA settings</Link>.
        </p>
      </div>
      <Button size="sm" variant="outline" disabled={sweep.isPending}
        onClick={() => sweep.mutate(undefined, { onSuccess: (r) => toast.success(`Sweep finished (${r.mode})`, { description: `${r.results.length} change${r.results.length === 1 ? '' : 's'}` }), onError: (e) => toast.error('Sweep failed', { description: e instanceof Error ? e.message : '' }) })}>
        {sweep.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run a sweep now
      </Button>
      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="px-3 py-2 font-medium">When</th><th className="px-3 py-2 font-medium">Project</th><th className="px-3 py-2 font-medium">Change</th><th className="px-3 py-2 text-right font-medium">Progress</th><th className="px-3 py-2 font-medium">Mode</th><th className="px-3 py-2 font-medium">Trigger</th></tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {isLoading && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>}
            {data?.items.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Nothing yet.</td></tr>}
            {data?.items.map((r) => (
              <tr key={r.id}>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground" title={r.at}>{fmtRelative(r.at)}</td>
                <td className="px-3 py-2"><Link to={projectPath(r.projectId)} className="hover:underline">{r.projectName}</Link></td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">{labels.projectStatus(r.fromStatus)} → <b>{labels.projectStatus(r.toStatus)}</b>{r.error && <div className="text-destructive">{r.error}</div>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{Number(r.progress)}%</td>
                <td className="px-3 py-2 text-xs">{r.mode === 'on' ? 'written' : 'dry run'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{r.trigger}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
